import React from "react";
import { AlertTriangle } from "lucide-react";

// Past this many files the warning names the first few and counts the rest
const MAX_NAMED_FILES = 3;

/**
 * The files uploaded to a discussion: the ones attached to its turns, plus any the uploader
 * stored for one of them. A file whose question_index is the next turn is still waiting in
 * the input box — it has not reached the discussion yet.
 */
export const discussionUploadedFiles = (conversation, contextFiles, threadIds) => {
  const turns = conversation ?? [];
  const files = [
    ...turns.flatMap((conv) => conv?.uploadedFiles ?? []),
    ...(contextFiles ?? []).filter(
      (file) => threadIds.includes(file.thread_id) && file.question_index < turns.length
    ),
  ];
  return files.filter((file, i) => files.findIndex((f) => f.file_id === file.file_id) === i);
};

const FileNames = ({ files }) => {
  const parts =
    files.length <= MAX_NAMED_FILES
      ? files.map((file) => file.file_name)
      : [
          ...files.slice(0, MAX_NAMED_FILES - 1).map((file) => file.file_name),
          `${files.length - (MAX_NAMED_FILES - 1)} more files`,
        ];

  return parts.map((part, i) => (
    <React.Fragment key={i}>
      {i > 0 && (i === parts.length - 1 ? " and " : ", ")}
      <span className="font-semibold">{part}</span>
    </React.Fragment>
  ));
};

/**
 * A scheduled prompt runs offline, in a conversation of its own, so the files uploaded to the
 * discussion it came from never reach it. Shown in the first schedule popup the user meets;
 * that popup's Cancel and Continue / Schedule buttons answer the question it asks.
 */
const ScheduleFilesWarning = ({ files }) => {
  if (!files?.length) {
    return null;
  }

  const single = files.length === 1;

  // contain:inline-size — the warning takes the popup's width and never sets it. The popups
  // size to their content, so its one long line would otherwise stretch them to their cap.
  return (
    <div
      role="alert"
      className="flex items-start gap-2.5 rounded-base bg-yellow px-4 py-3 text-[13px] leading-5 text-dark-blue-100 [contain:inline-size]"
    >
      <AlertTriangle size={16} className="mt-0.5 shrink-0 text-[#C9971A]" aria-hidden="true" />
      <div className="min-w-0 [overflow-wrap:anywhere]">
        <p className="font-semibold">Scheduled prompts don't support uploaded files</p>
        <p>
          This conversation includes <FileNames files={files} />. A scheduled prompt runs
          offline, so Mila won't be able to read {single ? "it" : "them"}. Do you want to
          proceed without the {single ? "file" : "files"}, or cancel the scheduling?
        </p>
      </div>
    </div>
  );
};

export default ScheduleFilesWarning;
